import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import {
  CUTTYSARK_COMMENT_MAX_LENGTH,
  CUTTYSARK_SCORE_MAX,
  CUTTYSARK_SCORE_MIN,
  type CuttysarkBallot,
  type CuttysarkLiveView,
} from '@cortex/domain-product-innovation-lab-types';
import { useSubmitCuttysarkBallot } from '../../../hooks/useCuttysark.js';
import { CandidateCard } from './CandidateCard.js';
import {
  EMPTY_SCORE_DRAFT,
  ballotRequestFromDraft,
  draftFromBallot,
  eligibleCandidates,
  parseScoreInput,
  remainingVotes,
  toggleVote,
  type CuttysarkBallotDraft,
  type CuttysarkScoreDraft,
} from './cuttysark-helpers.js';

/** @graph-connects none */
interface BallotFormProps {
  sessionId: string;
  view: CuttysarkLiveView;
  /** 前回送った投票（出し直しのときにフォームへ戻す）。 */
  initialBallot: CuttysarkBallot | null;
}

/**
 * 採点・投票フォーム。部門ごとに、自分以外の候補者を登録順（入力中に動かない）に並べ、
 * 業務成果・未来投資の点数と応援コメントを入れて、票数ぶん投票先を選ぶ。
 * 送信は全置換で、締切までは何度でも出し直せる。可否の判定は API が行い、
 * 弾かれたときは API のメッセージをそのまま出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の参加者が候補者を採点し、部門ごとに票を投じて送信するフォーム
 * @graph-connects useSubmitCuttysarkBallot [calls] 投票の送信
 * @graph-connects CandidateCard [calls] 候補者カードの表示
 * @graph-connects remainingVotes [calls] 残り票数の計算
 */
export function BallotForm({ sessionId, view, initialBallot }: BallotFormProps): ReactNode {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<CuttysarkBallotDraft>(() => draftFromBallot(initialBallot));
  const submit = useSubmitCuttysarkBallot(sessionId);
  const viewerEmail = view.viewer.email;

  const updateScore = (email: string, patch: Partial<CuttysarkScoreDraft>): void => {
    setDraft((prev) => ({
      ...prev,
      scores: {
        ...prev.scores,
        [email]: { ...(prev.scores[email] ?? EMPTY_SCORE_DRAFT), ...patch },
      },
    }));
  };

  const toggle = (divisionId: string, email: string, max: number): void => {
    setDraft((prev) => ({
      ...prev,
      votes: { ...prev.votes, [divisionId]: toggleVote(prev.votes[divisionId] ?? [], email, max) },
    }));
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate(ballotRequestFromDraft(draft));
      }}
    >
      <div>
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
          {t('cuttysark.ballot.heading')}
        </h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t('cuttysark.ballot.help')}</p>
      </div>

      {view.divisions.map((division) => {
        const eligible = eligibleCandidates(view.candidates, division.id, viewerEmail);
        const selected = draft.votes[division.id] ?? [];
        const max = Math.min(division.votesPerVoter, eligible.length);
        const remaining = remainingVotes(division, eligible.length, selected.length);
        return (
          <section key={division.id} className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('cuttysark.ballot.votesHeading', { division: division.name })}
              </h3>
              <Badge variant={remaining === 0 ? 'success' : 'warning'}>
                {remaining === 0
                  ? t('cuttysark.ballot.full')
                  : t('cuttysark.ballot.remaining', { count: remaining })}
              </Badge>
            </div>
            {eligible.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('cuttysark.ballot.noCandidates')}
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {eligible.map((candidate) => {
                  const score = draft.scores[candidate.email] ?? EMPTY_SCORE_DRAFT;
                  const isVoted = selected.includes(candidate.email);
                  return (
                    <CandidateCard
                      key={candidate.email}
                      candidate={candidate}
                      divisionName={division.name}
                      highlighted={isVoted}
                    >
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          size="sm"
                          inputMode="numeric"
                          min={CUTTYSARK_SCORE_MIN}
                          max={CUTTYSARK_SCORE_MAX}
                          label={t('cuttysark.ballot.achievement')}
                          value={score.achievement ?? ''}
                          onChange={(e) => {
                            updateScore(candidate.email, {
                              achievement: parseScoreInput(e.target.value),
                            });
                          }}
                        />
                        <Input
                          type="number"
                          size="sm"
                          inputMode="numeric"
                          min={CUTTYSARK_SCORE_MIN}
                          max={CUTTYSARK_SCORE_MAX}
                          label={t('cuttysark.ballot.future')}
                          value={score.future ?? ''}
                          onChange={(e) => {
                            updateScore(candidate.email, {
                              future: parseScoreInput(e.target.value),
                            });
                          }}
                        />
                      </div>
                      <label className="mt-2 block text-sm text-[var(--color-text-secondary)]">
                        {t('cuttysark.ballot.comment')}
                        <Textarea
                          className="mt-1"
                          rows={4}
                          maxLength={CUTTYSARK_COMMENT_MAX_LENGTH}
                          placeholder={t('cuttysark.ballot.commentPlaceholder')}
                          value={score.comment}
                          onChange={(e) => {
                            updateScore(candidate.email, { comment: e.target.value });
                          }}
                        />
                      </label>
                      <Button
                        type="button"
                        size="sm"
                        variant={isVoted ? 'primary' : 'secondary'}
                        className="mt-3 w-full"
                        disabled={!isVoted && remaining === 0}
                        aria-pressed={isVoted}
                        onClick={() => {
                          toggle(division.id, candidate.email, max);
                        }}
                      >
                        {isVoted ? `✓ ${t('cuttysark.ballot.voted')}` : t('cuttysark.ballot.vote')}
                      </Button>
                    </CandidateCard>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}

      {submit.isError && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {submit.error.message}
        </p>
      )}
      {submit.isSuccess && (
        <p className="text-sm text-[var(--color-success)]" role="status">
          {t('cuttysark.ballot.sent')}
        </p>
      )}
      <Button type="submit" fullWidth disabled={submit.isPending}>
        {view.viewer.hasSubmitted ? t('cuttysark.ballot.resubmit') : t('cuttysark.ballot.submit')}
      </Button>
    </form>
  );
}
